<!--
	CN-8312 V3: Project tray. Stacked-vertical mirror of the V2 procurement spec
	row (project-specifications-v2.component.html). Same projectCopy bindings,
	same updateProject() flow — only the layout differs.
-->
<div class="project-panel v3-project-panel" *ngIf="IS_NOT_NULL_OR_UNDEFINED(projectCopy)">
	<!-- Project Name -->
	<div class="panel-field">
		<div class="field-label">Project Name</div>
		<mat-form-field class="custom-form-field full-width" floatLabel="never" subscriptSizing="dynamic">
			<input [(ngModel)]="projectCopy.name" autocomplete="off" matInput />
		</mat-form-field>
	</div>

	<!-- Customer / Revenue Code -->
	<div
		class="panel-field"
		*ngIf="
			IS_NOT_NULL_OR_UNDEFINED(usersList) &&
			(IS_NOT_NULL_OR_UNDEFINED(projectCopy.customerDTO) || IS_EMPTY(projectCopy.revenueCode))
		"
	>
		<div class="field-label">Customer</div>
		<app-customer-popover
			*ngIf="accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS; else readonlyCustomer"
			(setCustomer)="setCustomer($event)"
			[customerSelected]="projectCopy.customerDTO"
		></app-customer-popover>
		<!-- ROLE_BASED_CUSTOMER_DATA_ACCESS=false: the popover is hidden but the
		     row still renders, so fall back to a read-only display rather than
		     leaving the label without a value. -->
		<ng-template #readonlyCustomer>
			<div class="field-value">{{ projectCopy.customerDTO?.name || '—' }}</div>
		</ng-template>
	</div>
	<div
		class="panel-field"
		*ngIf="IS_NOT_EMPTY(projectCopy.revenueCode) && IS_NULL_OR_UNDEFINED(projectCopy.customerDTO)"
	>
		<div class="field-label">Revenue Code</div>
		<mat-form-field class="custom-form-field full-width" floatLabel="never" subscriptSizing="dynamic">
			<input [(ngModel)]="projectCopy.revenueCode" autocomplete="off" matInput />
		</mat-form-field>
	</div>

	<!-- Lane -->
	<div class="panel-field">
		<div class="field-label">Lane</div>
		<div
			(click)="openLaneEditDialog()"
			(keydown.enter)="openLaneEditDialog()"
			(keydown.space)="$event.preventDefault(); openLaneEditDialog()"
			role="button"
			tabindex="0"
			aria-label="Edit lane"
			class="cursor-pointer lane-field v3-lane-field"
			fxLayout="row"
			fxLayoutAlign="space-between center"
		>
			<ng-container
				*ngIf="
					projectCopy.originCity ||
						projectCopy.originState ||
						projectCopy.destCity ||
						projectCopy.destState;
					else noLane
				"
			>
				<span class="primary-text text-ellipsis">
					{{
						projectCopy.originCity && projectCopy.originState
							? projectCopy.originCity + ', ' + projectCopy.originState
							: projectCopy.originCity || projectCopy.originState || ''
					}}
					→
					{{
						projectCopy.destCity && projectCopy.destState
							? projectCopy.destCity + ', ' + projectCopy.destState
							: projectCopy.destCity || projectCopy.destState || ''
					}}
					({{ projectEquipments.length > 0 ? projectEquipments.join(', ') : 'No Equipment' }})
				</span>
			</ng-container>
			<ng-template #noLane>
				<span class="lane-placeholder">No Lane</span>
			</ng-template>
			<mat-icon class="vertical-align-middle primary-text">keyboard_arrow_down</mat-icon>
		</div>
	</div>

	<!-- Appointment Required -->
	<div class="panel-field" fxLayout="row" fxLayoutAlign="space-between center">
		<mat-checkbox
			[(ngModel)]="projectCopy.appointmentRequired"
			[labelPosition]="'after'"
			color="primary"
		>
			Appointment Required
		</mat-checkbox>
		<mat-icon
			class="primary-light-color"
			matTooltip="When enabled, loads in this project will require appointment scheduling. This setting overrides the account-level Booking Assistant configuration."
			matTooltipClass="custom-tooltip"
		>
			info
		</mat-icon>
	</div>

	<!-- Assigned User -->
	<div class="panel-field">
		<div class="field-label">Assigned User</div>
		<app-user-popover
			(userChanged)="assigneeUpdated($event)"
			[isEnabled]="userPopoverComponent.enabled"
			[placeholder]="userPopoverComponent.placeholder"
			[selectedUsers]="userPopoverComponent.selectedUsers"
			[singleselect]="true"
			[usersList]="usersList"
		></app-user-popover>
	</div>

	<!--
		Project Type — same control / options as V2's procurement row 4
		(project-specifications-v2.component.html). Switching this away from
		Procurement will rebuild the page on the V1 workflow layout next
		render, since V3 is gated to non-Workflow projects.
	-->
	<div class="panel-field">
		<div class="field-label">Project Type</div>
		<mat-form-field class="custom-form-field full-width" floatLabel="never" subscriptSizing="dynamic">
			<mat-select [(ngModel)]="projectCopy.type" disableOptionCentering>
				<mat-option *ngFor="let option of procurementTypeOptions" [value]="option.value">
					{{ option.label }}
				</mat-option>
			</mat-select>
		</mat-form-field>
	</div>

	<mat-divider class="panel-divider"></mat-divider>

	<!-- Commodity -->
	<div class="panel-field">
		<div class="field-label">Commodity</div>
		<mat-form-field class="custom-form-field full-width" floatLabel="never" subscriptSizing="dynamic">
			<input [(ngModel)]="projectCopy.commodity" autocomplete="off" matInput />
		</mat-form-field>
	</div>

	<!-- Weight -->
	<div class="panel-field">
		<div class="field-label">Weight</div>
		<mat-form-field class="custom-form-field full-width" floatLabel="never" subscriptSizing="dynamic">
			<input [(ngModel)]="projectCopy.weight" autocomplete="off" matInput />
			<span matSuffix>lbs</span>
		</mat-form-field>
	</div>

	<!-- Load Volume -->
	<div class="panel-field">
		<div class="field-label">Load Volume</div>
		<mat-form-field class="custom-form-field full-width" floatLabel="never" subscriptSizing="dynamic">
			<mat-select [(ngModel)]="projectCopy.loadVolumeDuration" disableOptionCentering>
				<mat-option>Select</mat-option>
				<mat-option *ngFor="let duration of loadVolumeDuration" [value]="duration">
					{{ duration }}
				</mat-option>
			</mat-select>
		</mat-form-field>
	</div>

	<!-- Load Count -->
	<div class="panel-field">
		<div class="field-label">Load Count</div>
		<mat-form-field class="custom-form-field full-width" floatLabel="never" subscriptSizing="dynamic">
			<input [(ngModel)]="projectCopy.loadVolume" autocomplete="off" matInput />
		</mat-form-field>
	</div>

	<mat-divider class="panel-divider"></mat-divider>

	<!-- Max Buy -->
	<div class="panel-field">
		<div class="field-label">Max Buy</div>
		<mat-form-field class="custom-form-field full-width" floatLabel="never" subscriptSizing="dynamic">
			<mat-icon class="primary-light-color vertical-align-bottom" matPrefix>attach_money</mat-icon>
			<input
				[(ngModel)]="projectCopy.maxBuy"
				autocomplete="off"
				mask="separator"
				thousandSeparator=","
				matInput
			/>
		</mat-form-field>
	</div>

	<!-- Offer Rate -->
	<div class="panel-field">
		<div class="field-label">Offer Rate</div>
		<mat-form-field class="custom-form-field full-width" floatLabel="never" subscriptSizing="dynamic">
			<mat-icon class="primary-light-color vertical-align-bottom" matPrefix>attach_money</mat-icon>
			<input
				[(ngModel)]="projectCopy.offerRate"
				(ngModelChange)="projectCopy.offerRate = $event === '' ? null : $event"
				autocomplete="off"
				mask="separator"
				thousandSeparator=","
				matInput
			/>
		</mat-form-field>
	</div>

	<!-- Best Rate (read-only, click submits last bid) -->
	<div
		(click)="submitLastBid()"
		(keydown.enter)="submitLastBid()"
		(keydown.space)="$event.preventDefault(); submitLastBid()"
		role="button"
		tabindex="0"
		aria-label="Submit last bid as best rate"
		class="panel-field cursor-pointer"
	>
		<div class="field-label">Best Rate</div>
		<div class="font-size-14 underline" fxLayoutAlign="start center">
			<span *ngIf="IS_NOT_NULL_OR_UNDEFINED(projectCopy.bestRate)">
				{{ projectCopy.bestRate / 100 | currency: 'USD' : 'symbol' : '1.0-2' }}
			</span>
			<span *ngIf="IS_NULL_OR_UNDEFINED(projectCopy.bestRate)" class="font-size-14">
				<mat-icon
					[inline]="true"
					[matTooltip]="TOOLTIP_MSG.BEST_RATE"
					class="primary-text w-inherit"
					matTooltipClass="custom-tooltip"
				>
					info
				</mat-icon>
			</span>
		</div>
	</div>

	<!-- Stage -->
	<div class="panel-field">
		<div class="field-label">Stage</div>
		<mat-form-field class="custom-form-field full-width" floatLabel="never" subscriptSizing="dynamic">
			<mat-select [(ngModel)]="projectCopy.stageId" disableOptionCentering>
				<mat-option *ngFor="let stage of PROJECT_STAGES" [value]="stage.type">
					{{ stage.label }}
				</mat-option>
			</mat-select>
		</mat-form-field>
	</div>

	<!-- Update button (full width, pinned to bottom of stack) -->
	<div class="panel-field v3-update-row">
		<button
			(click)="updateProject()"
			*ngIf="!isUpdatingProject"
			[disabled]="!isProjectUpdated()"
			color="primary"
			mat-raised-button
			class="full-width"
		>
			UPDATE
		</button>
		<div *ngIf="isUpdatingProject" fxLayoutAlign="center center">
			<mat-spinner [diameter]="30"></mat-spinner>
		</div>
	</div>
</div>

<div class="project-panel-empty" *ngIf="!projectCopy">
	<p>No project data available</p>
</div>
